import type { AssetRef } from "@kss/schema";
import { useEffect, useRef } from "react";
import { useLoroSelector } from "../../sync/hooks";
import { MediaShell } from "../media/MediaShell";
import { derivativeUrl, useAsset } from "../media/useAsset";
import type { BlockComponentProps } from "../registry";
import { bodyMap } from "../textField";
import { useT } from "../../i18n";

/** Audio block (spec §8.8): wavesurfer.js with precomputed peaks from `waveform_json` when present. */
export function AudioBlock({ handle, treeId, node, readOnly }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const asset = (useLoroSelector(handle.doc, () => (body?.get("asset") as AssetRef | null) ?? null, (a, b) => a?.hash === b?.hash) ?? null) as AssetRef | null;
  const { upload, derivatives, stored, localUrl } = useAsset(asset?.hash ?? null);
  const ref = useRef<HTMLDivElement>(null);
  const src = stored ? `/api/assets/${asset?.hash}` : localUrl;
  const peaksUrl = derivativeUrl(derivatives, "waveform_json");

  useEffect(() => {
    const el = ref.current;
    if (!el || !src) return;
    let ws: import("wavesurfer.js").default | null = null;
    let cancelled = false;
    void (async () => {
      const WaveSurfer = (await import("wavesurfer.js")).default;
      let peaks: number[][] | undefined;
      let duration: number | undefined;
      if (peaksUrl) {
        try {
          const j = (await (await fetch(peaksUrl, { credentials: "same-origin" })).json()) as { peaks: number[]; sampleRate: number; samplesPerPixel: number };
          peaks = [j.peaks];
          duration = asset?.durationMs ? asset.durationMs / 1000 : (j.peaks.length * j.samplesPerPixel) / j.sampleRate;
        } catch {
          /* fall back to client decode */
        }
      }
      if (cancelled) return;
      // wavesurfer 7 draws and seeks but has no play button of its own: the browser's audio controls
      // play and pause, in the reader's language
      ws = WaveSurfer.create({ container: el, url: src, peaks, duration, height: 64, waveColor: "#9c9890", progressColor: "var(--color-accent)", normalize: true, mediaControls: true });
    })();
    return () => {
      cancelled = true;
      ws?.destroy();
    };
  }, [src, peaksUrl, asset?.durationMs]);

  if (!body) return null;
  return (
    <MediaShell handle={handle} treeId={treeId} body={body} accept="audio/*" readOnly={readOnly} asset={asset} upload={upload}>
      {src ? <div ref={ref} data-testid="audio-wave" /> : <div className="text-sm text-neutral-500">{t("media.offline", { name: asset?.name ?? "" })}</div>}
    </MediaShell>
  );
}
